import * as React from 'react';
import { useRouter } from 'next/router';
import { z } from 'zod';

export const StepName = z.enum([
  'pre_qualification',
  'location',
  'basic_information',
  'extra_information',
  'prices',
  'photos',
  'complete',
]);
export type StepName = z.infer<typeof StepName>;

export const StepRouteMap: Record<StepName, string> = {
  pre_qualification: '/pre_qualification',
  location: '/location',
  prices: '/prices',
  basic_information: '/basic_information',
  extra_information: '/extra_information',
  photos: '/photos',
  complete: '/complete',
};

const pathMap: Record<
  StepName,
  | { back: StepName; forward: undefined }
  | { back: undefined; forward: StepName }
  | {
      back: StepName;
      forward: StepName;
    }
> = {
  pre_qualification: {
    back: undefined,
    forward: 'location',
  },
  location: {
    back: 'pre_qualification',
    forward: 'prices',
  },
  prices: {
    back: 'location',
    forward: 'basic_information',
  },
  basic_information: {
    back: 'location',
    forward: 'extra_information',
  },
  extra_information: {
    back: 'basic_information',
    forward: 'photos',
  },
  photos: {
    back: 'extra_information',
    forward: 'complete',
  },
  complete: {
    back: 'photos',
    forward: undefined,
  },
};

const stepsTextsMap: Record<
  StepName,
  { title: string; description: { desktop: string; mobile: string } }
> = {
  pre_qualification: {
    title: 'Comencemos',
    description: {
      desktop: 'Ayúdanos con estas preguntas',
      mobile: 'Ayúdanos con estas preguntas',
    },
  },
  location: {
    title: 'Confirma la dirección',
    description: {
      desktop: 'Empecemos con la ubicación',
      mobile: 'Empecemos con la ubicación',
    },
  },
  prices: {
    title: 'Detalles publicación',
    description: {
      desktop: 'Definamos los detalles principales',
      mobile: 'Definamos los detalles principales',
    },
  },
  basic_information: {
    title: 'Información general',
    description: {
      desktop: 'Datos clave de la publicación',
      mobile: 'Datos clave de la publicación',
    },
  },
  extra_information: {
    title: 'Información adicional',
    description: {
      desktop: 'Otros atributos del inmueble',
      mobile: 'Otros atributos del inmueble',
    },
  },
  photos: {
    title: 'Agregar fotos',
    description: {
      desktop: 'Por último, agreguemos las fotos',
      mobile: 'Por último, agreguemos las fotos',
    },
  },
  complete: {
    title: 'Registro completo',
    description: {
      desktop: 'Nuestros expertos revisarán tu publicación',
      mobile: 'Nuestros expertos revisarán tu publicación',
    },
  },
};

export function usePublicationId() {
  const router = useRouter();
  const query = router.query;
  return query['publication-id'] as string;
}

export function usePublicationStep() {
  const router = useRouter();
  return React.useMemo(() => {
    const step = Object.keys(StepRouteMap).find((key) =>
      router.pathname.includes(StepRouteMap[key])
    );
    return step as StepName;
  }, [router.pathname]);
}

export function usePublicationRouter() {
  const router = useRouter();

  const currentStep = usePublicationStep();
  const publicationId = usePublicationId();

  const goToNextStep = async () => {
    const stepPath = pathMap[currentStep].forward;
    const hasStep = stepPath !== undefined;

    if (!hasStep) return;

    return router.push(`/listing-publications/${publicationId}/${stepPath}`);
  };

  const goToPreviousStep = async () => {
    const stepPath = pathMap[currentStep].back;
    const hasStep = stepPath !== undefined;

    if (!hasStep) return;

    return router.push(`/listing-publications/${publicationId}/${stepPath}`);
  };

  const hasGoBack = pathMap[currentStep].back !== undefined;

  const goToPublicationsPage = () => router.push('/listing-publications');

  return {
    currentStep: {
      name: currentStep,
      index: Object.keys(StepRouteMap).indexOf(currentStep),
    },
    steps: stepsTextsMap,
    goToNextStep,
    goToPreviousStep,
    goToPublicationsPage,
    isLastStep: currentStep === 'complete',
    hasGoBack,
    hasNextStep: pathMap[currentStep].forward !== undefined,
  };
}
